import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronsUpDown } from 'lucide-react';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { cn } from '@/lib/utils';
import { JapaneseVehiclePlateInput } from './JapaneseVehiclePlateInput';

type TriggerProps = Omit<
  React.ButtonHTMLAttributes<HTMLButtonElement>,
  'children' | 'onBlur' | 'onChange' | 'type' | 'value'
>;

interface JapaneseVehiclePlatePopoverInputProps extends TriggerProps {
  value?: string | null;
  onChange: (value: string) => void;
  onBlur?: (event?: React.FocusEvent<HTMLButtonElement>) => void;
  open?: boolean;
  defaultOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  placeholder?: string;
  cancelLabel?: string;
  confirmLabel?: string;
  editorLabel?: string;
}

export const JapaneseVehiclePlatePopoverInput = React.forwardRef<
  HTMLButtonElement,
  JapaneseVehiclePlatePopoverInputProps
>(
  (
    {
      value,
      onChange,
      onBlur,
      open: controlledOpen,
      defaultOpen = false,
      onOpenChange,
      placeholder,
      cancelLabel,
      confirmLabel,
      editorLabel,
      disabled,
      className,
      onFocus,
      onClick,
      onKeyDown,
      ...triggerProps
    },
    ref,
  ) => {
    const { t } = useTranslation('common');
    const [uncontrolledOpen, setUncontrolledOpen] = React.useState(defaultOpen);
    const [draftValue, setDraftValue] = React.useState(value ?? '');
    const triggerRef = React.useRef<HTMLButtonElement | null>(null);
    const open = controlledOpen ?? uncontrolledOpen;
    const summary =
      value?.trim() ||
      placeholder ||
      t('efs.common.vehicle_plate.input_prompt', {
        defaultValue: '車番を入力',
      });

    const setOpen = React.useCallback(
      (next: boolean) => {
        if (controlledOpen === undefined) {
          setUncontrolledOpen(next);
        }
        onOpenChange?.(next);
      },
      [controlledOpen, onOpenChange],
    );

    const openEditor = React.useCallback(() => {
      setDraftValue(value ?? '');
      setOpen(true);
    }, [setOpen, value]);

    const closeAndRestoreFocus = React.useCallback(() => {
      setOpen(false);
      window.setTimeout(() => triggerRef.current?.focus(), 0);
    }, [setOpen]);

    const discardAndClose = React.useCallback(() => {
      setDraftValue(value ?? '');
      closeAndRestoreFocus();
    }, [closeAndRestoreFocus, value]);

    const commitAndClose = React.useCallback(() => {
      onChange(draftValue);
      closeAndRestoreFocus();
    }, [closeAndRestoreFocus, draftValue, onChange]);

    const preventDismissPropagation = React.useCallback((event: Event) => {
      event.preventDefault();
      event.stopPropagation();
    }, []);

    React.useEffect(() => {
      if (open) {
        setDraftValue(value ?? '');
      }
    }, [open, value]);

    return (
      <Dialog
        open={open}
        onOpenChange={(next) => {
          if (next) {
            openEditor();
          } else {
            discardAndClose();
          }
        }}
      >
        <DialogTrigger asChild>
          <button
            ref={(node) => {
              triggerRef.current = node;
              if (typeof ref === 'function') {
                ref(node);
              } else if (ref) {
                ref.current = node;
              }
            }}
            type="button"
            disabled={disabled}
            aria-haspopup="dialog"
            aria-expanded={open}
            data-state={open ? 'open' : 'closed'}
            className={cn(
              'flex h-9 w-full items-center justify-between rounded-md border border-input bg-transparent px-3 py-2 text-left text-sm shadow-sm focus:outline-none focus:ring-1 focus:ring-ring disabled:cursor-not-allowed disabled:opacity-50',
              !value?.trim() && 'text-muted-foreground',
              className,
            )}
            onClick={(event) => {
              onClick?.(event);
              if (!event.defaultPrevented && !disabled) openEditor();
            }}
            onFocus={(event) => {
              onFocus?.(event);
            }}
            onKeyDown={(event) => {
              onKeyDown?.(event);
              if (event.defaultPrevented || disabled) return;
              if (event.key === 'ArrowDown') {
                event.preventDefault();
                openEditor();
              }
            }}
            onBlur={(event) => onBlur?.(event)}
            {...triggerProps}
          >
            <span className="truncate">{summary}</span>
            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" aria-hidden="true" />
          </button>
        </DialogTrigger>
        {open ? (
          <DialogContent
            className="max-h-[min(90vh,42rem)] w-[calc(100vw-2rem)] max-w-[42rem] overflow-y-auto p-3 sm:p-4"
            onInteractOutside={preventDismissPropagation}
            onPointerDownOutside={preventDismissPropagation}
            onEscapeKeyDown={(event) => {
              event.preventDefault();
              event.stopPropagation();
              discardAndClose();
            }}
          >
            <div className="space-y-3">
              <DialogTitle className="pr-8 text-sm font-semibold">
                {editorLabel ??
                  t('efs.common.vehicle_plate.editor_title', {
                    defaultValue: '車番入力',
                  })}
              </DialogTitle>
              <DialogDescription className="text-xs text-efs-text-muted">
                {t('efs.common.vehicle_plate.editor_hint', {
                  defaultValue: '地域・分類番号・かな・一連指定番号を入力してください。',
                })}
              </DialogDescription>
              <JapaneseVehiclePlateInput
                id={triggerProps.id ? `${triggerProps.id}-editor` : undefined}
                value={draftValue}
                disabled={disabled}
                onBlur={() => onBlur?.()}
                onChange={setDraftValue}
              />
              <div className="flex justify-end gap-2">
                <Button type="button" variant="outline" onClick={discardAndClose}>
                  {cancelLabel ?? t('efs.common.actions.cancel', { defaultValue: '取消' })}
                </Button>
                <Button type="button" onClick={commitAndClose}>
                  {confirmLabel ?? t('efs.common.vehicle_plate.done', { defaultValue: '完了' })}
                </Button>
              </div>
            </div>
          </DialogContent>
        ) : null}
      </Dialog>
    );
  },
);

JapaneseVehiclePlatePopoverInput.displayName = 'JapaneseVehiclePlatePopoverInput';
